import { useState, useEffect, Suspense, lazy } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import Book from './components/book/Book'
import Navigation from './components/ui/Navigation'
import EditorPage from './editor/EditorPage'
import { Header } from './components/ui/Header'
import { Paywall } from './components/ui/Paywall'
import { LoadingFallback } from './components/ui/LoadingFallback'
import { Library } from './pages/Library'
import { CanvasErrorBoundary } from './components/ui/CanvasErrorBoundary'
import { useAuthStore } from './stores/authStore'
import { useBookStore } from './stores/bookStore'
import { useIsMobile, useSwipe } from './hooks/useMobile'
import './App.css'

// P7-01: 页面级懒加载 — 非首屏页面全部 lazy load
const Account = lazy(() => import('./pages/Account').then(m => ({ default: m.Account })))
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })))
const Payment = lazy(() => import('./pages/Payment').then(m => ({ default: m.Payment })))
const ParentDashboard = lazy(() => import('./pages/ParentDashboard').then(m => ({ default: m.ParentDashboard })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const ChildrenPage = lazy(() => import('./pages/ChildrenPage').then(m => ({ default: m.ChildrenPage })))
const AiExplorer = lazy(() => import('./ai-explorer').then(m => ({ default: m.default })))
const AiGenerator = lazy(() => import('./ai-generator').then(m => ({ default: m.default })))
const AiHistory = lazy(() => import('./pages/AiHistory').then(m => ({ default: m.default })))

// ============================================================
// 2D 降级阅读器（Canvas 崩溃时使用）— P4-04 移动端适配版
// ============================================================
function Fallback2DReader() {
  const { currentBook, currentPage, setCurrentPage, bookLoading, bookError } = useBookStore()
  const isMobile = useIsMobile()

  const { handleTouchStart, handleTouchEnd } = useSwipe(
    () => setCurrentPage(Math.min((currentBook?.pages?.length || 1) - 1, currentPage + 1)), // swipe left = next
    () => setCurrentPage(Math.max(0, currentPage - 1)) // swipe right = prev
  )

  useEffect(() => {
    if (!currentBook) {
      const { fetchBook } = useBookStore.getState()
      fetchBook('brave-little-hedgehog')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (bookLoading) {
    return (
      <div style={{
        width: '100vw', height: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#1a1a2e', color: '#fff', flexDirection: 'column'
      }}>
        <div style={{ fontSize: isMobile ? '16px' : '18px', opacity: 0.8 }}>正在加载绘本...</div>
      </div>
    )
  }

  if (bookError || !currentBook) {
    return (
      <div style={{
        width: '100vw', height: '100vh', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: '#1a1a2e', color: '#fff'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: isMobile ? '14px' : '14px', opacity: 0.7, marginBottom: '20px' }}>
            {bookError || '绘本加载失败，请检查网络连接'}
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px', borderRadius: '8px', border: 'none',
              background: '#4ade80', color: '#fff', cursor: 'pointer', fontSize: '16px'
            }}
          >
            重新加载
          </button>
        </div>
      </div>
    )
  }

  const page = currentBook.pages?.[currentPage]
  const totalPages = currentBook.pages?.length || 1
  const containerPadding = isMobile ? '12px' : '20px'
  const titleFontSize = isMobile ? '16px' : '18px'
  const contentFontSize = isMobile ? '18px' : '20px'
  const contentPadding = isMobile ? '20px' : '30px'
  const btnPadding = isMobile ? '10px 18px' : '12px 24px'

  return (
    <div 
      style={{
        width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column',
        background: '#1a1a2e', color: '#fff', overflow: 'hidden',
        touchAction: 'pan-y' // 允许垂直滚动，水平翻页由 useSwipe 处理
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* 标题栏 */}
      <div style={{
        padding: `12px ${containerPadding}`, borderBottom: '1px solid #333',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexShrink: 0
      }}>
        <div style={{ fontSize: titleFontSize, fontWeight: 'bold', color: '#FFD54F' }}>
          {currentBook.title}
        </div>
        <div style={{ fontSize: '14px', opacity: 0.6 }}>
          第 {currentPage + 1} / {totalPages} 页
        </div>
      </div>

      {/* 内容区 */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: containerPadding, overflow: 'auto',
        WebkitOverflowScrolling: 'touch' // iOS 平滑滚动
      }}>
        <div style={{ maxWidth: '800px', width: '100%' }}>
          <div style={{
            background: '#2d2d44', borderRadius: isMobile ? '12px' : '16px', 
            padding: contentPadding,
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '14px', opacity: 0.6, marginBottom: '12px' }}>
              {page?.title || `第 ${currentPage + 1} 页`}
            </div>
            <p style={{ 
              fontSize: contentFontSize, lineHeight: '1.8', 
              color: '#e0e0e0', margin: 0,
              wordBreak: 'break-word' // 防止中文长文本溢出
            }}>
              {page?.content || '内容加载中...'}
            </p>
          </div>

          {page?.scene_3d?.elements && (
            <div style={{
              background: '#252538', borderRadius: '12px', padding: '16px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '12px', opacity: 0.5, marginBottom: '8px' }}>场景元素</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {page.scene_3d.elements.map((el: any, i: number) => (
                  <span key={i} style={{
                    background: '#3d3d5c', borderRadius: '6px', padding: '4px 10px',
                    fontSize: '13px'
                  }}>
                    {el.id}
                  </span>
                ))}
              </div>
            </div>
          )}

          {page?.interactions && page.interactions.length > 0 && (
            <div style={{ background: '#2a2a40', borderRadius: '12px', padding: '16px' }}>
              <div style={{ fontSize: '12px', opacity: 0.5, marginBottom: '8px' }}>可交互元素</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {page.interactions.map((it: any, i: number) => (
                  <span key={i} style={{
                    background: '#4a3a2a', borderRadius: '6px', padding: '4px 10px',
                    fontSize: '13px', color: '#FFB74D'
                  }}>
                    {it.target}: {it.action}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 底部导航 */}
      <div style={{
        padding: '12px', display: 'flex', justifyContent: 'center', gap: '16px',
        borderTop: '1px solid #333', background: '#1a1a2e',
        flexShrink: 0
      }}>
        <button
          onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
          disabled={currentPage === 0}
          style={{
            padding: btnPadding, borderRadius: '8px', border: 'none',
            background: currentPage === 0 ? '#444' : '#4ade80',
            color: '#fff', cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
            fontSize: '16px', flex: 1, maxWidth: '120px'
          }}
        >
          上一页
        </button>
        <span style={{ 
          display: 'flex', alignItems: 'center', fontSize: '16px', 
          minWidth: '80px', justifyContent: 'center' 
        }}>
          {currentPage + 1} / {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
          disabled={currentPage === totalPages - 1}
          style={{
            padding: btnPadding, borderRadius: '8px', border: 'none',
            background: currentPage === totalPages - 1 ? '#444' : '#4ade80',
            color: '#fff', cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer',
            fontSize: '16px', flex: 1, maxWidth: '120px'
          }}
        >
          下一页
        </button>
      </div>

      {/* 提示条 */}
      <div style={{
        padding: '6px', textAlign: 'center', fontSize: '11px', opacity: 0.4,
        background: '#151520', flexShrink: 0
      }}>
        3D 渲染模式暂时不可用，已切换至 2D 阅读模式 · 
        <a href="?mode=library" style={{ color: '#4ade80' }}>浏览绘本图书馆</a>
      </div>
    </div>
  )
}
// ============================================================
// 3D Canvas 阅读器（包裹在 Error Boundary 中）
// ============================================================
function ThreeDReader() {
  const { currentPage, currentBook } = useBookStore()

  return (
    <div id="canvas-container" className="relative" style={{ width: '100vw', height: '100vh' }}>
      <Header />
      <Canvas
        camera={{ position: [0, 0, 8], fov: 45 }}
        gl={{ antialias: true, alpha: true }}
        shadows
        style={{ width: '100%', height: '100%' }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.3} />
          <directionalLight position={[5, 10, 5]} intensity={1} castShadow />
          <Book />
          <OrbitControls
            enablePan={false}
            enableZoom={true}
            minDistance={5}
            maxDistance={15}
          />
        </Suspense>
      </Canvas>

      {/* UI Overlay */}
      <div className="ui-overlay" style={{ position: 'absolute', top: 0, left: 0, width: '100%', pointerEvents: 'none' }}>
        <div className="page-indicator" style={{
          position: 'absolute', top: '80px', left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.5)', color: '#fff', padding: '8px 16px',
          borderRadius: '20px', fontSize: '14px'
        }}>
          Page {currentPage + 1} / {currentBook?.pages?.length || 10}
        </div>
        <div className="controls-hint" style={{
          position: 'absolute', bottom: '140px', left: '50%', transform: 'translateX(-50%)',
          color: 'rgba(255,255,255,0.5)', fontSize: '12px'
        }}>
          Drag to orbit · Scroll to zoom
        </div>
      </div>

      <Navigation />

      <div style={{
        position: 'absolute', top: '80px', right: '20px', display: 'flex', gap: '8px',
        zIndex: 100
      }}>
        <button
          onClick={() => window.location.href = '?mode=ai-generator'}
          style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: 'rgba(255,152,0,0.5)', color: '#fff', cursor: 'pointer',
            fontSize: '14px', backdropFilter: 'blur(10px)'
          }}
        >
          🎨 AI 创作
        </button>
        <button
          onClick={() => window.location.href = '?mode=ai-explorer'}
          style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: 'rgba(124,77,255,0.5)', color: '#fff', cursor: 'pointer',
            fontSize: '14px', backdropFilter: 'blur(10px)'
          }}
        >
          🚀 AI 探索
        </button>
        <button
          onClick={() => window.location.href = '?mode=library'}
          style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: 'rgba(0,0,0,0.5)', color: '#fff', cursor: 'pointer',
            fontSize: '14px', backdropFilter: 'blur(10px)'
          }}
        >
          📖 图书馆
        </button>
        <button
          onClick={() => window.location.href = '?mode=account'}
          style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none',
            background: 'rgba(0,0,0,0.5)', color: '#fff', cursor: 'pointer',
            fontSize: '14px', backdropFilter: 'blur(10px)'
          }}
        >
          👤 账户
        </button>
      </div>
    </div>
  )
}

// ============================================================
// 主应用
// ============================================================
function App() {
  const [mode, setMode] = useState<'reader' | 'editor' | 'library' | 'account' | 'admin' | 'payment' | 'ai-explorer' | 'ai-generator' | 'ai-history' | 'parent' | 'settings' | 'children'>(() => {
    const m = new URLSearchParams(window.location.search).get('mode')
    return (m as any) || 'reader'
  })
  const [showPaywall, setShowPaywall] = useState(false)
  const { isLoggedIn, fetchMe } = useAuthStore()
  const { currentBook, currentPage } = useBookStore()

  useEffect(() => {
    fetchMe()
  }, [])

  // 付费墙：未登录/未订阅用户读到预览页上限时触发
  useEffect(() => {
    if (!currentBook?.pages?.length) return
    const full = currentBook.can_read_full ?? true
    const previewLimit = currentBook.max_preview_pages ?? currentBook.pages.length
    if (!full && currentPage >= previewLimit) {
      setShowPaywall(true)
    }
  }, [currentPage, currentBook])

  // 同步 URL 参数
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (mode === 'reader') params.delete('mode')
    else params.set('mode', mode)
    const qs = params.toString()
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname)
  }, [mode])

  if (mode === 'editor') {
    const params = new URLSearchParams(window.location.search)
    const sessionId = params.get('session')
    return <EditorPage sessionId={sessionId ?? undefined} onExit={() => setMode('reader')} />
  }

  if (mode === 'library') {
    return <Library />
  }

  if (mode === 'account') {
    return (
      <Suspense fallback={<LoadingFallback pageName="账户" />}>
        <Account />
      </Suspense>
    )
  }

  if (mode === 'admin') {
    return (
      <Suspense fallback={<LoadingFallback pageName="管理后台" />}>
        <Admin />
      </Suspense>
    )
  }

  if (mode === 'payment') {
    return (
      <Suspense fallback={<LoadingFallback pageName="支付" />}>
        <Payment />
      </Suspense>
    )
  }

  if (mode === 'ai-explorer') {
    return (
      <Suspense fallback={<LoadingFallback pageName="AI 探索" />}>
        <AiExplorer />
      </Suspense>
    )
  }

  if (mode === 'ai-generator') {
    return (
      <Suspense fallback={<LoadingFallback pageName="AI 创作" />}>
        <AiGenerator />
      </Suspense>
    )
  }

  if (mode === 'ai-history') {
    return (
      <Suspense fallback={<LoadingFallback pageName="AI 历史" />}>
        <AiHistory />
      </Suspense>
    )
  }

  if (mode === 'parent') {
    return (
      <Suspense fallback={<LoadingFallback pageName="家长看板" />}>
        <ParentDashboard />
      </Suspense>
    )
  }

  if (mode === 'settings') {
    return (
      <Suspense fallback={<LoadingFallback pageName="设置" />}>
        <SettingsPage />
      </Suspense>
    )
  }

  // P7-03: 多孩档案页面
  if (mode === 'children') {
    return (
      <Suspense fallback={<LoadingFallback pageName="儿童档案" />}>
        <ChildrenPage />
      </Suspense>
    )
  }

  // 默认阅读器：3D Canvas 包裹在 Error Boundary 中
  return (
    <>
      <CanvasErrorBoundary fallback={<Fallback2DReader />}>
        <ThreeDReader />
      </CanvasErrorBoundary>

      {/* 付费墙（在 Error Boundary 外部，确保弹窗不受 Canvas 崩溃影响） */}
      {showPaywall && (
        <Paywall
          onClose={() => setShowPaywall(false)}
          onSubscribe={() => {
            if (!isLoggedIn) {
              alert('请先登录')
              return
            }
            window.location.href = '?mode=payment'
          }}
        />
      )}
    </>
  )
}

export default App
